// src/pages/Services/MobileApp.jsx
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Close,
  ArrowForward,
  CheckCircle,
  Phone,
  Email,
  Android,
  Apple,
  Laptop,
  Devices,
  Speed,
  BatteryChargingFull,
  Wifi,
  Gamepad,
  School,
  AttachMoney,
  SportsEsports,
  Restaurant,
  Movie,
  Business,
  Code,
  PhoneIphone,
  Tablet,
} from "@mui/icons-material";
import styles from "./MobileApp.module.css";

// Services Data
const servicesData = [
  {
    id: 1,
    title: "Cross-Platform Development",
    icon: <Devices sx={{ fontSize: 40 }} />,
    description:
      "Build once, deploy everywhere. We develop apps that work seamlessly across iOS, Android, and Windows platforms.",
    features: [
      "Single codebase",
      "Consistent UI/UX",
      "Faster development",
      "Cost-effective",
    ],
    color: "#8cc61d",
  },
  {
    id: 2,
    title: "Native App Development",
    icon: <PhoneIphone sx={{ fontSize: 40 }} />,
    description:
      "Leverage platform-specific features for optimal performance and user experience.",
    features: [
      "Platform-specific APIs",
      "Best performance",
      "Native UI/UX",
      "Full device access",
    ],
    color: "#4a90d9",
  },
  {
    id: 3,
    title: "Performance Optimization",
    icon: <Speed sx={{ fontSize: 40 }} />,
    description:
      "Ensure your app runs smoothly with optimal performance, memory management, and battery efficiency.",
    features: [
      "Memory optimization",
      "Battery efficiency",
      "Network optimization",
      "Fast load times",
    ],
    color: "#9b59b6",
  },
  {
    id: 4,
    title: "App Maintenance & Support",
    icon: <Code sx={{ fontSize: 40 }} />,
    description:
      "Ongoing support, updates, and maintenance to keep your app running perfectly.",
    features: [
      "Bug fixes",
      "Feature updates",
      "Performance monitoring",
      "24/7 support",
    ],
    color: "#f39c12",
  },
];

// Industry Sectors
const sectors = [
  { icon: <School />, label: "Education", color: "#8cc61d" },
  { icon: <SportsEsports />, label: "Gaming", color: "#4a90d9" },
  { icon: <Restaurant />, label: "Leisure", color: "#f39c12" },
  { icon: <AttachMoney />, label: "Financial", color: "#2ecc71" },
  { icon: <Movie />, label: "Entertainment", color: "#9b59b6" },
  { icon: <Business />, label: "Custom Design", color: "#e74c3c" },
];

// Platform Icons - Fixed Windows icon
const platforms = [
  { icon: <Apple sx={{ fontSize: 48 }} />, label: "iOS", color: "#000000" },
  {
    icon: <Android sx={{ fontSize: 48 }} />,
    label: "Android",
    color: "#3DDC84",
  },
  {
    icon: <Laptop sx={{ fontSize: 48 }} />,
    label: "Windows",
    color: "#0078D4",
  },
];

// Stats Data
const statsData = [
  { number: "5+", label: "Years Experience", icon: <Code /> },
  { number: "50+", label: "Apps Developed", icon: <PhoneIphone /> },
  { number: "4.9", label: "Average Rating", icon: <CheckCircle /> },
  { number: "100%", label: "Client Satisfaction", icon: <Speed /> },
];

const MobileApp = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const handleOpenDialog = (service) => {
    setSelectedService(service);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedService(null);
  };

  return (
    <Box className={styles.mobilePage}>
      {/* ===== HERO SECTION WITH BACKGROUND IMAGE & ANIMATIONS ===== */}
      <Box className={styles.heroSection}>
        {/* Animated Background Elements */}
        <Box className={styles.animatedBg}>
          {/* Floating Particles */}
          <Box className={`${styles.particle} ${styles.particle1}`}></Box>
          <Box className={`${styles.particle} ${styles.particle2}`}></Box>
          <Box className={`${styles.particle} ${styles.particle3}`}></Box>
          <Box className={`${styles.particle} ${styles.particle4}`}></Box>
          <Box className={`${styles.particle} ${styles.particle5}`}></Box>
          <Box className={`${styles.particle} ${styles.particle6}`}></Box>
          <Box className={`${styles.particle} ${styles.particle7}`}></Box>
          <Box className={`${styles.particle} ${styles.particle8}`}></Box>

          {/* Floating Mobile App Icons */}
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon1}`}>
            📱
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon2}`}>
            📲
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon3}`}>
            ⚡
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon4}`}>
            💻
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon5}`}>
            🎯
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon6}`}>
            🚀
          </Box>

          {/* Floating Shapes */}
          <Box className={`${styles.shape} ${styles.shape1}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape2}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape3}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>

          {/* Floating Lines */}
          <Box className={`${styles.line} ${styles.line1}`}></Box>
          <Box className={`${styles.line} ${styles.line2}`}></Box>

          {/* App Pattern Dots */}
          <Box className={styles.appPattern}>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
            <Box className={styles.appDot}></Box>
          </Box>
        </Box>

        <Container maxWidth="lg">
          <Box className={styles.heroContent}>
            <Typography
              variant="h1"
              className={styles.heroTitle}
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
              }}
            >
              Professional <span className={styles.highlight}>Mobile Apps</span>
            </Typography>
            <Typography variant="h6" className={styles.heroSubtitle}>
              Custom mobile app development for iOS, Android, and Windows
              platforms
            </Typography>

            {/* Scroll Indicator */}
            <Box className={styles.scrollIndicator}>
              <Box className={styles.scrollMouse}>
                <Box className={styles.scrollWheel}></Box>
              </Box>
              <Typography variant="caption" className={styles.scrollText}>
                Scroll to explore
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Intro Section */}
      <Box className={styles.introSection}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Box className={styles.introContent}>
                <Chip
                  label="💡 About Our Services"
                  className={styles.sectionChip}
                  size="small"
                />
                <Typography
                  variant="h2"
                  className={styles.sectionTitle}
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
                  }}
                >
                  <span className={styles.highlight}>Expert</span> Mobile App
                  Development
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  With many years of experience in building mobile apps
                  including{" "}
                  <strong>
                    gaming, leisure, education, financial and custom designs
                  </strong>
                  , our developers keep up with the latest trends and
                  guidelines, essential for professional app development
                  services.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We are continually improving our services and we are able to
                  provide solutions as follows:
                </Typography>
                <Box className={styles.solutionsList}>
                  <Box className={styles.solutionItem}>
                    <CheckCircle className={styles.solutionCheck} />
                    <Typography variant="body2">
                      Cross-device compatibility
                    </Typography>
                  </Box>
                  <Box className={styles.solutionItem}>
                    <CheckCircle className={styles.solutionCheck} />
                    <Typography variant="body2">
                      Cross-platform development
                    </Typography>
                  </Box>
                  <Box className={styles.solutionItem}>
                    <CheckCircle className={styles.solutionCheck} />
                    <Typography variant="body2">
                      Performance and memory limitations
                    </Typography>
                  </Box>
                  <Box className={styles.solutionItem}>
                    <CheckCircle className={styles.solutionCheck} />
                    <Typography variant="body2">
                      Battery consumption optimization
                    </Typography>
                  </Box>
                  <Box className={styles.solutionItem}>
                    <CheckCircle className={styles.solutionCheck} />
                    <Typography variant="body2">
                      Network speed optimization
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box className={styles.statsWrapper}>
                <Paper elevation={0} className={styles.statsPaper}>
                  <Grid container spacing={2}>
                    {statsData.map((stat, index) => (
                      <Grid size={{ xs: 6, sm: 6 }} key={index}>
                        <Box className={styles.statCard}>
                          <Box className={styles.statIcon}>{stat.icon}</Box>
                          <Typography
                            variant="h3"
                            className={styles.statNumber}
                          >
                            {stat.number}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={styles.statLabel}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Platforms Section */}
      <Box className={styles.platformsSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="🖥️ Platforms We Support"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Native & Cross-Platform{" "}
              <span className={styles.highlight}>Development</span>
            </Typography>
            <Typography variant="body1" className={styles.sectionSubtitle}>
              We provide mobile development services for a range of mobile
              platforms
            </Typography>
          </Box>

          <Box className={styles.platformsWrapper}>
            {platforms.map((platform, index) => (
              <Paper key={index} elevation={0} className={styles.platformCard}>
                <Box
                  className={styles.platformIcon}
                  sx={{ color: platform.color }}
                >
                  {platform.icon}
                </Box>
                <Typography variant="h5" className={styles.platformLabel}>
                  {platform.label}
                </Typography>
              </Paper>
            ))}
          </Box>

          <Box className={styles.platformNote}>
            <Typography variant="body2" className={styles.noteText}>
              We resolve all issues meticulously and deliver only faultless
              mobile solutions that can smoothly operate on mobile devices
              regardless of specifications.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Sectors Section */}
      <Box className={styles.sectorsSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="🏢 Industries We Serve"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Our <span className={styles.highlight}>Expertise</span>
            </Typography>
            <Typography variant="body1" className={styles.sectionSubtitle}>
              Building mobile apps for diverse industries
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {sectors.map((sector, index) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                <Box
                  className={styles.sectorCard}
                  sx={{
                    "&:hover": {
                      borderColor: sector.color,
                      boxShadow: `0 8px 32px ${sector.color}20`,
                    },
                  }}
                >
                  <Box
                    className={styles.sectorIcon}
                    sx={{ color: sector.color }}
                  >
                    {sector.icon}
                  </Box>
                  <Typography variant="body2" className={styles.sectorLabel}>
                    {sector.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box className={styles.servicesSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="⚡ Our Services"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              What We <span className={styles.highlight}>Offer</span>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {servicesData.map((service) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={service.id}>
                <Card className={styles.serviceCard}>
                  <CardContent>
                    <Box
                      className={styles.serviceIcon}
                      sx={{
                        background: `${service.color}15`,
                        color: service.color,
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography variant="h6" className={styles.serviceTitle}>
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.serviceDescription}
                    >
                      {service.description}
                    </Typography>
                    <Box className={styles.serviceFeatures}>
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <Box key={idx} className={styles.serviceFeature}>
                          <CheckCircle
                            className={styles.featureCheck}
                            sx={{ color: service.color }}
                          />
                          <Typography variant="caption">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Button
                      variant="contained"
                      className={styles.serviceBtn}
                      onClick={() => handleOpenDialog(service)}
                      endIcon={<ArrowForward />}
                      fullWidth
                      sx={{ background: service.color }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box className={styles.ctaSection}>
        <Container maxWidth="lg">
          <Box className={styles.ctaContent}>
            <Typography variant="h3" className={styles.ctaTitle}>
              Ready to Build Your{" "}
              <span className={styles.highlight}>Mobile App</span>?
            </Typography>
            <Typography variant="body1" className={styles.ctaText}>
              Let's create a powerful mobile application that will engage your
              users and grow your business.
            </Typography>
            <Box className={styles.ctaButtons}>
              <Button
                variant="contained"
                className={styles.ctaPrimaryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
              <Button
                variant="outlined"
                className={styles.ctaSecondaryBtn}
                startIcon={<Email />}
              >
                Email Us
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Service Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        className={styles.dialog}
      >
        {selectedService && (
          <>
            <DialogTitle className={styles.dialogTitle}>
              <Box className={styles.dialogHeader}>
                <Box
                  className={styles.dialogIcon}
                  sx={{
                    background: `${selectedService.color}15`,
                    color: selectedService.color,
                  }}
                >
                  {selectedService.icon}
                </Box>
                <Typography variant="h5" className={styles.dialogTitleText}>
                  {selectedService.title}
                </Typography>
                <IconButton
                  onClick={handleCloseDialog}
                  className={styles.dialogClose}
                >
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Typography variant="body1" className={styles.dialogDescription}>
                {selectedService.description}
              </Typography>
              <Divider className={styles.dialogDivider} />
              <Typography
                variant="subtitle1"
                fontWeight={600}
                className={styles.dialogFeaturesTitle}
              >
                Key Features:
              </Typography>
              <Box className={styles.dialogFeaturesList}>
                {selectedService.features.map((feature, index) => (
                  <Box key={index} className={styles.dialogFeature}>
                    <CheckCircle
                      className={styles.dialogFeatureCheck}
                      sx={{ color: selectedService.color }}
                    />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
                className={styles.dialogCancelBtn}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className={styles.dialogInquiryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MobileApp;
